import React, { useMemo } from 'react';
import { motion } from 'framer-motion';
import { Worker3D } from './Worker3D';
import { AIWorker, Phase } from '../types';

// ═══════════════════════════════════════════════════════════
//  World3D — isometric "dev floor" scene
//  Pure CSS 3D + framer-motion. Places every worker on a
//  tiled glowing floor with a holographic task card floating
//  above them. The user sees the whole team working live.
// ═══════════════════════════════════════════════════════════

interface Props {
  workers: AIWorker[];
  phases: Phase[];
  onWorkerClick?: (worker: AIWorker) => void;
}

const ROLE_LABEL: Record<string, string> = {
  coder: 'Coder',
  orchestrator: 'Orchestrator',
  tester: 'Tester',
};

const statusLabel: Record<string, string> = {
  idle: 'Idle',
  working: 'Working',
  done: 'Done',
  error: 'Error',
  waiting: 'Waiting',
};

const statusColor: Record<string, string> = {
  idle: '#64748b',
  working: '#22c55e',
  done: '#3b82f6',
  error: '#ef4444',
  waiting: '#eab308',
};

export const World3D: React.FC<Props> = ({ workers, phases, onWorkerClick }) => {
  // Pick the "live" phase — first not accepted — to show above the scene
  const livePhase = useMemo(() => {
    const notDone = phases.find((p) => p.status !== 'accepted' && p.status !== 'rejected');
    return notDone || phases[0];
  }, [phases]);

  const liveModules = useMemo(() => {
    if (!livePhase) return [];
    return livePhase.modules.slice(0, 6);
  }, [livePhase]);

  // Distribute workers around the floor in a fan arrangement
  const positions = useMemo(() => {
    const n = workers.length || 1;
    const radius = 200;
    return workers.map((_, i) => {
      const t = n === 1 ? 0 : i / (n - 1); // 0..1
      const angle = -0.55 + t * 1.1; // spread ~±30°
      return {
        x: Math.sin(angle) * radius,
        z: -Math.cos(angle) * radius * 0.6,
      };
    });
  }, [workers]);

  return (
    <div className="world3d">
      <div className="world3d-header">
        <h3 className="world3d-title">Command Floor</h3>
        <div className="world3d-phase-pill">
          {livePhase ? (
            <>
              <span className="world3d-phase-name">{livePhase.name}</span>
              <span className="world3d-phase-status" data-status={livePhase.status || 'uploaded'}>
                {livePhase.status || 'uploaded'}
              </span>
            </>
          ) : (
            <span className="world3d-phase-name">No active phase</span>
          )}
        </div>
      </div>

      <div className="world3d-viewport">
        {/* ambient starfield */}
        <div className="world3d-stars" />
        {/* Aurora backdrop */}
        <div className="world3d-aurora" />

        <div className="world3d-stage">
          {/* Floor grid */}
          <div className="world3d-floor">
            <div className="world3d-floor-grid" />
            <div className="world3d-floor-glow" />
          </div>

          {/* Back wall with live phase modules as a holo wall of tickets */}
          <div className="world3d-wall">
            <div className="world3d-wall-label">LIVE PHASE</div>
            <div className="world3d-wall-grid">
              {liveModules.map((m) => {
                const done = m.status === 'completed';
                const fix = m.status === 'needs_fix' || m.status === 'failed';
                const prog = m.status === 'in_progress' || m.status === 'self_check';
                return (
                  <motion.div
                    key={m.id}
                    className="world3d-ticket"
                    data-state={done ? 'done' : fix ? 'fix' : prog ? 'prog' : 'idle'}
                    initial={{ opacity: 0, y: 6 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <span className="world3d-ticket-name">{m.name}</span>
                    <span className="world3d-ticket-status">{m.status.replace('_', ' ')}</span>
                  </motion.div>
                );
              })}
              {liveModules.length === 0 && (
                <div className="world3d-wall-empty">Upload a phase.md to populate the board</div>
              )}
            </div>
          </div>

          {/* Workers on floor */}
          <div className="world3d-actors">
            {workers.map((w, i) => {
              const pos = positions[i];
              const taskTitle = w.currentTask?.title || (w.status === 'idle' ? 'Awaiting tasks' : statusLabel[w.status]);
              return (
                <div
                  key={w.id}
                  className="world3d-actor"
                  style={{ transform: `translate3d(${pos.x}px, 0, ${pos.z}px)` }}
                  onClick={() => onWorkerClick?.(w)}
                >
                  {/* floating task card */}
                  <motion.div
                    className="world3d-actor-card"
                    initial={{ opacity: 0, y: 4 }}
                    animate={{ opacity: 1, y: 0 }}
                  >
                    <span className="world3d-actor-name">{w.name}</span>
                    <span
                      className="world3d-actor-dot"
                      style={{ background: statusColor[w.status], boxShadow: `0 0 8px ${statusColor[w.status]}` }}
                    />
                    <span className="world3d-actor-role">{ROLE_LABEL[w.role] || w.role}</span>
                    <span className="world3d-actor-task" title={taskTitle}>
                      {taskTitle.substring(0, 44)}
                    </span>
                    {w.currentTask && (
                      <div className="world3d-actor-progress">
                        <div
                          className="world3d-actor-progress-fill"
                          style={{
                            width: `${w.currentTask.progress}%`,
                            background: statusColor[w.status],
                          }}
                        />
                      </div>
                    )}
                  </motion.div>

                  {/* the 3D avatar itself */}
                  <div className="world3d-actor-avatar">
                    <Worker3D workerId={w.id} status={w.status} />
                  </div>

                  {/* Beam connecting avatar to ticket wall when working */}
                  {w.status === 'working' && (
                    <motion.div
                      className="world3d-beam"
                      style={{ background: `linear-gradient(180deg, ${statusColor.working}, transparent)` }}
                      animate={{ opacity: [0.3, 0.9, 0.3] }}
                      transition={{ duration: 1.4, repeat: Infinity }}
                    />
                  )}
                </div>
              );
            })}
          </div>
        </div>
      </div>

      <div className="world3d-legend">
        {Object.entries(statusLabel).map(([k, v]) => (
          <div key={k} className="world3d-legend-item">
            <span className="world3d-legend-dot" style={{ background: statusColor[k], boxShadow: `0 0 6px ${statusColor[k]}` }} />
            <span>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
};
